import { useMemo } from 'react';

import { getTreatmentWithConfigJSON } from './getTreatmentWithConfigJSON';
import { parseConfig } from './parseConfig';
import { useGetAttributesWithValues } from './useGetAttributesWithValues';
import { useGetSplitClient } from './useGetSplitClient';

import type { Feature } from './features';
import type { GetTreatmentReturn } from './types';

/**
 * Returns the treatment and config for a given feature.
 *
 * Use useTreatment instead of useGetTreatment when you need to evaluate a
 * feature on every render.
 */
export function useTreatment<F extends Feature>(
  feature: F
): GetTreatmentReturn<F> {
  const getSplitClient = useGetSplitClient();
  const getAttributesWithValues = useGetAttributesWithValues();

  const { treatment, configJSON, isReady } = getTreatmentWithConfigJSON(
    feature,
    getSplitClient,
    getAttributesWithValues
  );

  const config = useMemo(() => {
    return configJSON ? parseConfig(feature, configJSON) : undefined;
  }, [feature, configJSON]);

  return {
    treatment,
    config,
    isReady,
  } as GetTreatmentReturn<F>;
}
